import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/auth'
import { getUserFromToken } from '../../utils/getUserFromToken'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'

/**
 * Detecção de sessão expirada DENTRO do fluxo do React (OP-3462).
 *
 * O guard de render (`verifyJwt`) só reavalia quando algo re-renderiza — com a
 * aba parada o token vencia e a tela ficava "logada" sem carregar nada. Este
 * hook cobre o buraco:
 *  - agenda um timer para o instante exato do `exp` do JWT;
 *  - reavalia ao voltar o foco/visibilidade da aba (navegadores estrangulam
 *    timers em background — ex.: usuário volta do almoço).
 *
 * NUNCA navega nem toca em window.location — apenas devolve o estado; quem
 * decide navegar (via <Navigate>) é o componente que consome. Isso mantém o
 * fluxo do /auth intacto (a lição do interceptor que quebrou o app do produtor).
 */
export const useSessionExpired = (): boolean => {
  const { verifyJwt, cookies } = useAuth()
  const jwt: string = cookies?.userJWT ?? ''
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    if (LOCAL_EXECUTION || !jwt) return undefined

    const check = () => {
      if (!verifyJwt()) setExpired(true)
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const { exp } = getUserFromToken(jwt)
      if (exp) {
        const ms = exp * 1000 - Date.now()
        if (ms <= 0) check()
        else timer = setTimeout(check, ms + 1000) // +1s de folga p/ clock skew
      }
    } catch {
      /* token ilegível — o guard de render existente cobre */
    }

    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [jwt, verifyJwt])

  return expired
}
